import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { errorMessage } from "@/lib/errorMessage";
import type {
  Organization,
  CreateOrganizationRequest,
  UpdateOrganizationRequest
} from "@/types/organizations";

/**
 * Throw for an RFC 9457 ProblemDetails body (`{ title, status >= 400, errors? }`).
 * Used on the create and update paths, where a model-validation 400 arrives in this
 * shape and would otherwise be toasted as a successful save.
 */
export function throwIfProblemDetails(body: unknown): void {
  if (!body || typeof body !== "object") return;
  const pd = body as { title?: unknown; status?: unknown; errors?: unknown };
  if (typeof pd.status !== "number" || pd.status < 400 || typeof pd.title !== "string") return;
  const fieldMessages =
    pd.errors && typeof pd.errors === "object"
      ? Object.values(pd.errors as Record<string, unknown>)
          .flat()
          .filter((m): m is string => typeof m === "string")
      : [];
  const error = new Error(fieldMessages.length > 0 ? fieldMessages.join(" ") : pd.title);
  (error as any).status = pd.status;
  throw error;
}

/**
 * The text to show for a failed organisation save. A service refusal (rename refused, duplicate
 * subdomain) comes back as the VALIDATION_ERROR envelope, whose `message` is the generic
 * "Validation failed" and whose `details` carry the sentence — so `errorDetails` wins.
 */
export function organizationSaveErrorMessage(error: unknown, fallback: string): string {
  const details = (error as { errorDetails?: unknown } | null)?.errorDetails;
  if (typeof details === "string" && details.trim()) return details.trim();
  return errorMessage(error, fallback);
}

export function useOrganizations() {
  const {
    data: organizations = [],
    isLoading,
    error
  } = useQuery<Organization[]>({
    queryKey: ["/organizations"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/organizations");
      return response.data || response; // Handle both wrapper and direct response
    }
  });

  return {
    organizations,
    isLoading,
    error
  };
}

// Hook for getting single organization
export function useOrganization(id: string | null) {
  const {
    data: organization,
    isLoading,
    error
  } = useQuery<Organization>({
    queryKey: ["/organizations", id],
    queryFn: async () => {
      if (!id) throw new Error("Organization ID is required");
      const response = await apiRequest("GET", `/organizations/${id}`);
      return response.data || response;
    },
    enabled: !!id
  });

  return {
    organization,
    isLoading,
    error
  };
}

// Hook for creating organizations
export function useCreateOrganization() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    // Never auto-retry a create: a retried POST can create a duplicate organization.
    retry: false,
    mutationFn: async (data: CreateOrganizationRequest) => {
      const response = await apiRequest("POST", "/organizations", data);
      // ASP.NET validation failures come back as a bare ProblemDetails (not the
      // standard envelope), which the shared parser passes through as "success".
      throwIfProblemDetails(response);
      return response.data || response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/organizations"] });
      toast({
        title: "Organization created",
        description: "Organization has been created successfully."
      });
    },
    onError: (error: unknown) => {
      toast({
        title: "Error",
        description: organizationSaveErrorMessage(error, "Failed to create organization. Please try again."),
        variant: "destructive"
      });
    }
  });
}

// Hook for updating organizations
export function useUpdateOrganization() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateOrganizationRequest }) => {
      const response = await apiRequest("PUT", `/organizations/${id}`, data);
      return response.data || response;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/organizations"] });
      queryClient.invalidateQueries({ queryKey: ["/organizations", variables.id] });
      toast({
        title: "Organization updated",
        description: "Organization has been updated successfully."
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update organization. Please try again.",
        variant: "destructive"
      });
    }
  });
}

// Hook for fetching organization-specific vendors (if this endpoint exists)
export function useOrganizationVendors(organizationId: string | null) {
  const {
    data: vendors = [],
    isLoading,
    error
  } = useQuery({
    queryKey: [`/organizations/${organizationId}/vendors`],
    queryFn: async () => {
      return await apiRequest("GET", `/organizations/${organizationId}/vendors`);
    },
    enabled: !!organizationId && organizationId !== 'all'
  });

  return {
    vendors,
    isLoading,
    error
  };
}